import React, { useState, useEffect, useMemo } from 'react';
import { motion } from 'framer-motion';
import Header from '../../components/ui/Header';
import BlogHero from './components/BlogHero';
import SearchBar from './components/SearchBar';
import CategoryFilter from './components/CategoryFilter';
import FeaturedArticle from './components/FeaturedArticle';
import BlogCard from './components/BlogCard';
import NewsletterSignup from './components/NewsletterSignup';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import { Link } from 'react-router-dom';


const BlogPage = () => {
  const [activeCategory, setActiveCategory] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [filteredArticles, setFilteredArticles] = useState([]);

  // Mock data for blog categories
  const categories = [
    {
      id: 'all',
      name: 'All Articles',
      description: 'Browse all our content',
      icon: 'Grid3X3',
      count: 200,
      isNew: false
    },
    {
      id: 'technical',
      name: 'Technical Tutorials',
      description: 'Step-by-step coding guides',
      icon: 'Code',
      count: 85,
      isNew: false
    },
    {
      id: 'industry',
      name: 'Industry Insights',
      description: 'Trends and analysis',
      icon: 'TrendingUp',
      count: 42,
      isNew: true
    },
    {
      id: 'ai-ml',
      name: 'AI & Machine Learning',
      description: 'Artificial intelligence deep dives',
      icon: 'Brain',
      count: 38,
      isNew: true
    },
    {
      id: 'web3',
      name: 'Web3 & Blockchain',
      description: 'Decentralized technologies',
      icon: 'Link',
      count: 25,
      isNew: false
    },
    {
      id: 'devops',
      name: 'DevOps & Cloud',
      description: 'Infrastructure and deployment',
      icon: 'Cloud',
      count: 31,
      isNew: false
    },
    {
      id: 'ui-ux',
      name: 'UI/UX Design',
      description: 'User experience insights',
      icon: 'Palette',
      count: 28,
      isNew: false
    }
  ];

  // Mock data for featured article
  const featuredArticle = {
    id: 'featured-1',
    title: `Building Scalable React Applications with Advanced State Management Patterns`,
    excerpt: `Discover how to architect React applications that can handle complex state requirements while maintaining performance and developer experience. Learn about advanced patterns, best practices, and real-world implementation strategies.`,
    image: 'https://images.unsplash.com/photo-1555066931-4365d14bab8c?w=800&h=600&fit=crop',
    author: {
      name: 'Sarah Chen',
      role: 'Senior Frontend Architect',
      avatar: 'https://randomuser.me/api/portraits/women/32.jpg'
    },
    category: {
      name: 'Technical Tutorial',
      icon: 'Code'
    },
    publishedAt: '2025-09-28',
    readTime: 12,
    views: 15420,
    likes: 342,
    tags: ['React', 'State Management', 'Architecture', 'Performance'],
    slug: 'scalable-react-state-management',
    codeSnippet: `const useAdvancedState = () => {\n  return useMemo(() => state, [deps]);\n};`,
    hasCodePreview: true
  };

  // Mock data for blog articles
  const blogArticles = [
    {
      id: 1,
      title: `The Future of JavaScript: ES2025 Features That Will Change How We Code`,
      excerpt: `Explore the upcoming JavaScript features that promise to revolutionize web development. From pattern matching to temporal API, discover what's coming next in the JavaScript ecosystem.`, image: 'https://images.unsplash.com/photo-1627398242454-45a1465c2479?w=600&h=400&fit=crop',
      author: {
        name: 'Alex Rodriguez', role: 'JavaScript Expert', avatar: 'https://randomuser.me/api/portraits/men/45.jpg'
      },
      category: {
        name: 'Technical Tutorial', icon: 'Code'
      },
      publishedAt: '2025-10-02',
      readTime: 8,
      views: 8750,
      likes: 156,
      comments: 23,
      tags: ['JavaScript', 'ES2025', 'Web Development'],
      slug: 'javascript-es2025-features',
      codeSnippet: `match (value) {\n  when Number => 'numeric'\n}`,
      hasCodePreview: true,
      isFeatured: false
    },
    {
      id: 2,
      title: `AI-Powered Development Tools: Transforming the Software Engineering Landscape`,
      excerpt: `How artificial intelligence is reshaping software development workflows, from code generation to automated testing. Discover the tools that are changing the game for developers worldwide.`,
      image: 'https://images.unsplash.com/photo-1677442136019-21780ecad995?w=600&h=400&fit=crop',
      author: {
        name: 'Dr. Maya Patel', role: 'AI Research Lead', avatar: 'https://randomuser.me/api/portraits/women/28.jpg'
      },
      category: {
        name: 'AI & Machine Learning', icon: 'Brain'
      },
      publishedAt: '2025-09-30',
      readTime: 10,
      views: 12300,
      likes: 289,
      comments: 45,
      tags: ['AI', 'Development Tools', 'Automation', 'Machine Learning'],
      slug: 'ai-powered-development-tools',
      codeSnippet: `ai.generateCode(prompt)`,
      hasCodePreview: true,
      isFeatured: true
    },
    {
      id: 3,
      title: `Micro-Frontends Architecture: Building Scalable Enterprise Applications`,
      excerpt: `Learn how to implement micro-frontends architecture for large-scale applications. Explore patterns, tools, and best practices for managing distributed frontend systems.`,
      image: 'https://images.unsplash.com/photo-1558494949-ef010cbdcc31?w=600&h=400&fit=crop',
      author: {
        name: 'James Wilson', role: 'Solutions Architect', avatar: 'https://randomuser.me/api/portraits/men/52.jpg'
      },
      category: {
        name: 'Industry Insights', icon: 'TrendingUp'
      },
      publishedAt: '2025-09-25',
      readTime: 15,
      views: 6890,
      likes: 134,
      comments: 18,
      tags: ['Architecture', 'Micro-frontends', 'Enterprise', 'Scalability'],
      slug: 'micro-frontends-architecture', codeSnippet: `import('./microfrontend')`,
      hasCodePreview: true,
      isFeatured: false
    },
    {
      id: 4,
      title: `Web3 Development Fundamentals: Smart Contracts and DApps`,
      excerpt: `A comprehensive guide to building decentralized applications. From smart contract development to frontend integration, master the fundamentals of Web3 development.`,
      image: 'https://images.unsplash.com/photo-1639762681485-074b7f938ba0?w=600&h=400&fit=crop',
      author: {
        name: 'Elena Kowalski', role: 'Blockchain Developer', avatar: 'https://randomuser.me/api/portraits/women/41.jpg'
      },
      category: {
        name: 'Web3 & Blockchain', icon: 'Link'
      },
      publishedAt: '2025-09-22',
      readTime: 18,
      views: 9450,
      likes: 201,
      comments: 32,
      tags: ['Web3', 'Blockchain', 'Smart Contracts', 'DApps'],
      slug: 'web3-development-fundamentals',
      codeSnippet: `contract MyToken {\n  mapping(address => uint) balances;\n}`,
      hasCodePreview: true,
      isFeatured: false
    },
    {
      id: 5,
      title: `DevOps Best Practices: CI/CD Pipelines for Modern Applications`,
      excerpt: `Streamline your development workflow with advanced CI/CD strategies. Learn how to implement robust deployment pipelines that ensure reliability and speed.`,
      image: 'https://images.unsplash.com/photo-1667372393119-3d4c48d07fc9?w=600&h=400&fit=crop',
      author: {
        name: 'Marcus Thompson', role: 'DevOps Engineer', avatar: 'https://randomuser.me/api/portraits/men/38.jpg'
      },
      category: {
        name: 'DevOps & Cloud', icon: 'Cloud'
      },
      publishedAt: '2025-09-20',
      readTime: 12,
      views: 7230,
      likes: 167,
      comments: 25,
      tags: ['DevOps', 'CI/CD', 'Automation', 'Cloud'],
      slug: 'devops-cicd-best-practices',
      codeSnippet: `pipeline {\n  stages {\n    stage('Build') { ... }\n  }\n}`,
      hasCodePreview: true,
      isFeatured: false
    },
    {
      id: 6,
      title: `Design Systems at Scale: Creating Consistent User Experiences`,
      excerpt: `Build and maintain design systems that scale across multiple products and teams. Learn the principles, tools, and processes that make design systems successful.`,
      image: 'https://images.unsplash.com/photo-1561070791-2526d30994b5?w=600&h=400&fit=crop',
      author: {
        name: 'Sophie Martinez', role: 'Design System Lead', avatar: 'https://randomuser.me/api/portraits/women/35.jpg'
      },
      category: {
        name: 'UI/UX Design', icon: 'Palette'
      },
      publishedAt: '2025-09-18',
      readTime: 9,
      views: 5670,
      likes: 98,
      comments: 14,
      tags: ['Design Systems', 'UI/UX', 'Consistency', 'Scalability'],
      slug: 'design-systems-at-scale',
      codeSnippet: `<Button variant="primary" size="lg" />`,
      hasCodePreview: true,
      isFeatured: false
    }
  ];

  // Handle search functionality
  const handleSearch = async (query) => {
    setSearchQuery(query);
    if (!query?.trim()) {
      setSearchResults([]);
      return;
    }

    setIsSearching(true);

    // Simulate API search delay
    await new Promise(resolve => setTimeout(resolve, 500));

    const results = blogArticles?.filter(article =>
      article?.title?.toLowerCase()?.includes(query?.toLowerCase()) ||
      article?.excerpt?.toLowerCase()?.includes(query?.toLowerCase()) ||
      article?.tags?.some(tag => tag?.toLowerCase()?.includes(query?.toLowerCase())) ||
      article?.author?.name?.toLowerCase()?.includes(query?.toLowerCase())
    );

    setSearchResults(results);
    setIsSearching(false);
  };

  // Filter articles based on category and search
  useEffect(() => {
    let filtered = blogArticles;

    if (activeCategory !== 'all') {
      const categoryMap = {
        'technical': 'Technical Tutorial',
        'industry': 'Industry Insights',
        'ai-ml': 'AI & Machine Learning',
        'web3': 'Web3 & Blockchain',
        'devops': 'DevOps & Cloud',
        'ui-ux': 'UI/UX Design'
      };

      filtered = filtered?.filter(article =>
        article?.category?.name === categoryMap?.[activeCategory]
      );
    }

    if (searchQuery?.trim()) {
      filtered = searchResults;
    }

    setFilteredArticles(filtered);
  }, [activeCategory, searchQuery, searchResults]);

  return (
    <div className="min-h-screen bg-background">
      <Header />
      {/* Hero Section */}
      <BlogHero />
      {/* Search Section */}
      <section className="py-16 px-6">
        <div className="max-w-7xl mx-auto">
          <SearchBar
            onSearch={handleSearch}
            searchResults={searchResults}
            isSearching={isSearching}
          />
        </div>
      </section>
      {/* Featured Article */}
      <section className="py-16 px-6">
        <div className="max-w-7xl mx-auto">
          <FeaturedArticle article={featuredArticle} />
        </div>
      </section>
      {/* Main Content */}
      <section className="py-16 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-4 gap-8">
            {/* Sidebar */}
            <div className="lg:col-span-1">
              <div className="sticky top-24 space-y-8">
                <CategoryFilter
                  categories={categories}
                  activeCategory={activeCategory}
                  onCategoryChange={setActiveCategory}
                />

                {/* Newsletter Signup */}
                <NewsletterSignup />
              </div>
            </div>

            {/* Articles Grid */}
            <div className="lg:col-span-3">
              {/* Section Header */}
              <div className="flex items-center justify-between mb-8">
                <div>
                  <h2 className="text-2xl font-bold text-foreground mb-2">
                    {searchQuery ? `Search Results for "${searchQuery}"` :
                      activeCategory === 'all' ? 'Latest Articles' :
                        categories?.find(cat => cat?.id === activeCategory)?.name}
                  </h2>
                  <p className="text-muted-foreground">
                    {filteredArticles?.length} article{filteredArticles?.length !== 1 ? 's' : ''} found
                  </p>
                </div>

                {/* Sort Options */}
                <div className="flex items-center space-x-2">
                  <Icon name="SlidersHorizontal" size={16} className="text-muted-foreground" />
                  <select className="bg-card border border-border rounded-lg px-3 py-2 text-sm text-foreground">
                    <option>Latest</option>
                    <option>Most Popular</option>
                    <option>Most Liked</option>
                  </select>
                </div>
              </div>

              {/* Articles Grid */}
              {filteredArticles?.length > 0 ? (
                <div className="grid md:grid-cols-2 gap-8">
                  {filteredArticles?.map((article, index) => (
                    <BlogCard
                      key={article?.id}
                      article={article}
                      index={index}
                    />
                  ))}
                </div>
              ) : (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="text-center py-16"
                >
                  <Icon name="Search" size={48} className="text-muted-foreground mx-auto mb-4" />
                  <h3 className="text-xl font-semibold text-foreground mb-2">
                    No articles found
                  </h3>
                  <p className="text-muted-foreground mb-6">
                    Try adjusting your search terms or browse different categories
                  </p>
                  <button
                    onClick={() => {
                      setSearchQuery('');
                      setActiveCategory('all');
                    }}
                    className="text-accent hover:text-accent/80 font-medium"
                  >
                    View all articles
                  </button>
                </motion.div>
              )}

              {/* Load More */}
              {filteredArticles?.length > 0 && (
                <div className="text-center mt-12">
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    className="bg-card border border-border text-foreground px-8 py-3 rounded-xl font-medium hover:bg-muted/50 transition-all duration-300 shadow-3d"
                  >
                    Load More Articles
                  </motion.button>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>
      {/* Footer CTA */}
      <section className="py-16 px-6 bg-gradient-to-br from-accent/5 via-transparent to-success/5">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-3xl font-bold text-foreground mb-4">
              Ready to Start Your Next Project?
            </h2>
            <p className="text-muted-foreground text-lg mb-8">
              Let's transform your ideas into exceptional digital experiences
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center space-y-4 sm:space-y-0 sm:space-x-4">
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="bg-accent text-accent-foreground px-8 py-3 rounded-xl font-medium hover:bg-accent/90 transition-all duration-300 shadow-accent flex items-center space-x-2"
              >
                <Link to="/contact" className='flex gap-2'>
                  <Icon name="Rocket" size={16} />
                  <span>Start Your Project</span>
                </Link>
              </motion.button>
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="border border-accent/30 text-accent px-8 py-3 rounded-xl font-medium hover:bg-accent/10 transition-all duration-300 flex items-center space-x-2"
              >
                <Link to="/contact" className='flex gap-2'>
                  <Icon name="MessageCircle" size={16} />
                  <span>Get in Touch</span>
                </Link>
              </motion.button>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default BlogPage;